import guitarData from '@tombatossals/chords-db/lib/guitar.json';

// Guitar instrument config for the Chord SVG renderer
export const guitarInstrument = {
  strings: 6,
  fretsOnChord: 4,
  tunings: guitarData.tunings,
};

// Chromatic key names in display order (single source of truth)
export const chromaticKeys = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

// Maps display key names to database key names
const keyMap = {
  'C': 'C',
  'Db': 'Csharp',
  'D': 'D',
  'Eb': 'Eb',
  'E': 'E',
  'F': 'F',
  'F#': 'Fsharp',
  'G': 'G',
  'Ab': 'Ab',
  'A': 'A',
  'Bb': 'Bb',
  'B': 'B',
};

// Maps display quality names to database suffix names
const qualityMap = {
  'Maj': 'major',
  'Min': 'minor',
  'Dim': 'dim',
  'Dim7': 'dim7',
  'Aug': 'aug',
  'Aug7': 'aug7',
  'Aug9': 'aug9',
  '6': '6',
  'm6': 'm6',
  '6/9': '69',
  'm6/9': 'm69',
  '7': '7',
  '7b5': '7b5',
  '7b9': '7b9',
  '7#9': '7#9',
  '7sus4': '7sus4',
  'Maj7': 'maj7',
  'Maj7b5': 'maj7b5',
  'Maj7#5': 'maj7#5',
  'Min7': 'm7',
  'Min7b5': 'm7b5',
  'Min/Maj7': 'mmaj7',
  'Min/Maj7b5': 'mmaj7b5',
  '9': '9',
  '9b5': '9b5',
  '9#11': '9#11',
  'Maj9': 'maj9',
  'Min9': 'm9',
  'Min/Maj9': 'mmaj9',
  '11': '11',
  'Maj11': 'maj11',
  'Min11': 'm11',
  'Min/Maj11': 'mmaj11',
  '13': '13',
  'Maj13': 'maj13',
  'Sus2': 'sus2',
  'Sus4': 'sus4',
  'Add9': 'add9',
  'mAdd9': 'madd9',
  'Alt': 'alt',
};

// Maps display bass note names to database slash chord suffix format
const bassNoteMap = {
  'C': 'C',
  'Db': 'C#',
  'D': 'D',
  'Eb': 'D#',
  'E': 'E',
  'F': 'F',
  'F#': 'F#',
  'G': 'G',
  'Ab': 'G#',
  'A': 'A',
  'Bb': 'Bb',
  'B': 'B',
};

// All available quality display names (ordered for dropdown)
export const qualityOptions = [
  'Maj', 'Min', 'Dim', 'Aug', 'Sus2', 'Sus4',
  '6', 'm6', '6/9', 'm6/9',
  '7', '7b5', '7b9', '7#9', '7sus4',
  'Maj7', 'Maj7b5', 'Maj7#5', 'Min7', 'Min7b5', 'Min/Maj7', 'Min/Maj7b5',
  'Dim7', 'Aug7',
  '9', '9b5', '9#11', 'Maj9', 'Min9', 'Min/Maj9', 'Aug9', 'Add9', 'mAdd9',
  '11', 'Maj11', 'Min11', 'Min/Maj11',
  '13', 'Maj13',
  'Alt',
];

// Returns available bass note options for a given key
export function getAvailableBassNotes(displayKey) {
  const dbKey = keyMap[displayKey];
  if (!dbKey || !guitarData.chords[dbKey]) return ['No Bass'];

  const chords = guitarData.chords[dbKey];
  const bassNotes = new Set();

  for (const chord of chords) {
    if (chord.suffix.startsWith('/')) {
      const note = chord.suffix.slice(1);
      bassNotes.add(note);
    }
  }

  if (bassNotes.size === 0) return ['No Bass'];

  // Map DB note names back to display names
  const reverseBassMap = {};
  for (const [display, db] of Object.entries(bassNoteMap)) {
    reverseBassMap[db] = display;
  }

  const displayNotes = [];
  for (const note of bassNotes) {
    const displayName = reverseBassMap[note] || note;
    if (!displayNotes.includes(displayName)) {
      displayNotes.push(displayName);
    }
  }

  // Sort chromatically
  displayNotes.sort((a, b) => chromaticKeys.indexOf(a) - chromaticKeys.indexOf(b));

  return ['No Bass', ...displayNotes];
}

// Looks up chord position data from the local database
export function lookupChord(displayKey, displayQuality, displayBass) {
  const dbKey = keyMap[displayKey];
  if (!dbKey) return null;

  const keyChords = guitarData.chords[dbKey];
  if (!keyChords) return null;

  // Determine the suffix to look up
  let suffix;

  if (displayBass && displayBass !== 'No Bass' && displayBass !== 'Bass') {
    // Slash chord: look for /{bassNote} suffix
    const dbBassNote = bassNoteMap[displayBass];
    if (!dbBassNote) return null;

    // Check for minor slash chords first if quality is minor-related
    const qualitySuffix = qualityMap[displayQuality];
    if (qualitySuffix && (qualitySuffix.startsWith('m') && qualitySuffix !== 'major' && qualitySuffix !== 'maj7' && qualitySuffix !== 'maj9' && qualitySuffix !== 'maj11' && qualitySuffix !== 'maj13' && qualitySuffix !== 'maj7b5' && qualitySuffix !== 'maj7#5' && qualitySuffix !== 'madd9')) {
      suffix = `m/${dbBassNote}`;
      const minorMatch = keyChords.find(c => c.suffix === suffix);
      if (minorMatch) return minorMatch.positions[0];
    }

    // Fall back to major slash chord
    suffix = `/${dbBassNote}`;
  } else {
    suffix = qualityMap[displayQuality];
  }

  if (!suffix) return null;

  const match = keyChords.find(c => c.suffix === suffix);
  if (!match) return null;

  return match.positions[0];
}
